// Presenting (v9.8.0): the GM puts a handout up on every screen in its
// audience. Pure.
//
// A presentation lives ON THE HANDOUT (`presentedAt`, 0 = not presented),
// not in a table-wide document: only the audience can load the file, and
// the audience query already delivers the handout, stamp and all, to
// exactly those people - a late joiner included. Closing it is each
// viewer's own and never reaches the server: it dismisses THAT presentation
// (the handout and its stamp, as a pair), so presenting again opens it again.
// The browser keeps it per session, so a rejoin does not reopen it (2026-10-28).
import type { Handout } from './types';

/** The merge that puts a handout up. */
export function presentPatch(now: number): { presentedAt: number; updatedAt: number } {
  return { presentedAt: now, updatedAt: now };
}

/** The merge that takes it down, for everyone. */
export function endPresentPatch(now: number): { presentedAt: number; updatedAt: number } {
  return { presentedAt: 0, updatedAt: now };
}

/** Somebody besides the GM can see it, and it is up. */
const isUp = (h: Handout): boolean => h.presentedAt > 0 && h.audience.length > 0;

/** What is up in this feed: the newest presentation, or null. A feed holds only what its reader may see. */
export function presented(handouts: readonly Handout[]): Handout | null {
  return handouts
    .filter(isUp)
    .reduce<Handout | null>((top, h) => (top && top.presentedAt >= h.presentedAt ? top : h), null);
}

/** A presentation a viewer closed: the handout and the stamp it was closed at. */
export type Dismissed = { id: string; at: number } | null;

export function dismissalOf(h: Handout): Dismissed {
  return { id: h.id, at: h.presentedAt };
}

export function isDismissed(h: Handout, dismissed: Dismissed): boolean {
  return !!dismissed && dismissed.id === h.id && dismissed.at === h.presentedAt;
}
